import { createClient } from '@connectrpc/connect';
import { connectTransport } from '@mono/ui-runtime/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CapabilityLinkService } from '../../../../../../gen/ts/mono/capability/v1/capability_links_connect';
import { useAuth } from '../../../contexts/authContext';
import { useDebounce } from '../../../hooks/useDebounce';
import { deliveryClient } from '../../../lib/connect/clients';
import { contractsClient } from '../../Delivery/Registries/Contracts/api';
import { registryKeys } from '../queryKeys';
export const capabilityLinkClient = createClient(CapabilityLinkService, connectTransport);
export function useCapabilityLinks(capabilityId) {
    const { user } = useAuth();
    return useQuery({
        queryKey: [
            ...registryKeys.all,
            'capability-links',
            user?.tenant_id,
            user?.id,
            capabilityId
        ],
        queryFn: ()=>capabilityLinkClient.listCapabilityRequirementLinks({
                capabilityId
            }),
        enabled: !!capabilityId,
        retry: false
    });
}
export function useCapabilityProofs(capabilityId) {
    const { user } = useAuth();
    return useQuery({
        queryKey: [
            ...registryKeys.all,
            'capability-proofs',
            user?.tenant_id,
            user?.id,
            capabilityId
        ],
        queryFn: ()=>capabilityLinkClient.listCapabilityProofs({
                capabilityId
            }),
        enabled: !!capabilityId,
        retry: false
    });
}
export function useCapabilityLinkTargets(query, enabled) {
    const { user } = useAuth();
    const debounced = useDebounce(query, 250);
    const result = useQuery({
        queryKey: [
            ...registryKeys.all,
            'capability-targets',
            user?.tenant_id,
            user?.id,
            debounced
        ],
        queryFn: ()=>capabilityLinkClient.searchCapabilityLinkTargets({
                query: debounced,
                limit: 20
            }),
        enabled,
        retry: false
    });
    return {
        ...result,
        settling: query !== debounced
    };
}
export function useCapabilityLinkMutations() {
    const qc = useQueryClient();
    const refresh = ()=>qc.invalidateQueries({
            queryKey: registryKeys.all
        });
    const link = useMutation({
        mutationFn: (input)=>capabilityLinkClient.linkCapabilityToRequirement(input),
        onSuccess: refresh
    });
    const unlink = useMutation({
        mutationFn: (linkId)=>capabilityLinkClient.unlinkCapabilityRequirement({
                linkId
            }),
        onSuccess: refresh
    });
    const addProof = useMutation({
        mutationFn: (input)=>capabilityLinkClient.addCapabilityProof(input),
        onSuccess: refresh
    });
    const removeProof = useMutation({
        mutationFn: (id)=>capabilityLinkClient.removeCapabilityProof({
                id
            }),
        onSuccess: refresh
    });
    return {
        link,
        unlink,
        addProof,
        removeProof
    };
}
// Picker reads are scoped to the viewer just like the link receipts.
export function useProofAssociations(programSearch, contractSearch) {
    const { user } = useAuth();
    const programQuery = useDebounce(programSearch, 250);
    const contractQuery = useDebounce(contractSearch, 250);
    const programs = useQuery({
        queryKey: [
            ...registryKeys.all,
            'proof-programs',
            user?.tenant_id,
            user?.id,
            programQuery
        ],
        queryFn: ()=>deliveryClient.listPrograms({
                companyId: user?.tenant_id ?? '',
                search: programQuery
            }),
        retry: false
    });
    const contracts = useQuery({
        queryKey: [
            ...registryKeys.all,
            'proof-contracts',
            user?.tenant_id,
            user?.id,
            contractQuery
        ],
        queryFn: ()=>contractsClient.browseRegistryContracts({
                piid: contractQuery,
                limit: 30
            }),
        retry: false
    });
    return {
        programs,
        contracts
    };
}
export function useLinkCapabilities(enabled) {
    const { user } = useAuth();
    return useQuery({
        queryKey: [
            ...registryKeys.all,
            'link-capabilities',
            user?.tenant_id,
            user?.id
        ],
        queryFn: ()=>deliveryClient.listCapabilities({
                companyId: user?.tenant_id ?? ''
            }),
        enabled,
        retry: false
    });
}
